import { useMemo } from 'react';
import { USER_MESSAGES, PAYMENT_MESSAGES } from '../constants/messages';

/**
 * Custom hook to extract and process failing payments data
 * Separates business logic from presentation components
 * 
 * @param {Array} subscriptions - Array of subscription objects from Stripe
 * @param {Array} users - Array of user objects from database
 * @param {Array} prices - Array of price objects from Stripe
 * @returns {Array} Array of failing payment objects with customer details
 */
export const useFailingPayments = (subscriptions = [], users = [], prices = []) => {
  return useMemo(() => {
    return subscriptions
      .filter(sub => sub.paymentStatus && sub.paymentStatus.isFailingPayment)
      .map(sub => {
        // Find user by standardized customerId field (camelCase per project guidelines)
        const user = users.find(u => u.customerId === sub.customer);
        const price = prices.find(p => p.id === sub.productId);
        
        return {
          id: sub.latest_invoice?.id || sub.id,
          clientName: user ? `${user.firstName} ${user.lastName}` : USER_MESSAGES.CUSTOMER_NOT_FOUND,
          clientEmail: user?.email || USER_MESSAGES.EMAIL_NOT_AVAILABLE,
          amount: sub.paymentStatus.amountDue,
          reason: sub.paymentStatus.failureReason || PAYMENT_MESSAGES.PAYMENT_FAILED,
          attempts: sub.paymentStatus.attemptCount,
          daysSinceFailure: sub.paymentStatus.daysSinceFailure,
          lastAttempt: sub.paymentStatus.lastAttemptDate,
          subscriptionId: sub.id,
          invoiceId: sub.latest_invoice?.id,
          userId: user?.id,
          planName: price?.nickname || PAYMENT_MESSAGES.UNKNOWN_PLAN,
          planAmount: price ? price.price / 100 : 0
        };
      })
      .sort((a, b) => a.daysSinceFailure - b.daysSinceFailure);
  }, [subscriptions, users, prices]);
};

export default useFailingPayments;